import { type ElementRef, type ReactNode, type TextareaHTMLAttributes, forwardRef } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

const textareaVariants = tv({
  slots: {
    root: 'flex flex-col gap-1',
    label: 'text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300',
    textarea: [
      'w-full resize-y rounded-md border border-uniform-gray-300 bg-white px-3 py-2 text-sm',
      'text-uniform-gray-900 placeholder:text-uniform-gray-400',
      'dark:border-uniform-gray-600 dark:bg-uniform-gray-800 dark:text-uniform-gray-100',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
      'disabled:cursor-not-allowed disabled:bg-uniform-gray-50 disabled:text-uniform-gray-400'
    ],
    help: 'text-xs text-uniform-gray-600 dark:text-uniform-gray-400',
    error: 'text-xs text-red-500 dark:text-red-400'
  },
  variants: {
    hasError: {
      true: {
        textarea: ['border-red-500 dark:border-red-400', 'focus:border-red-500 focus:ring-red-500']
      }
    }
  }
});

type TextareaVariants = VariantProps<typeof textareaVariants>;

interface TextareaProps extends TextareaHTMLAttributes<ElementRef<'textarea'>> {
  label?: ReactNode;
  labelText?: string;
  helpText?: string;
  /** Marks the field invalid and renders the message in place of `helpText`. */
  error?: string;
}

const Textarea = forwardRef<ElementRef<'textarea'>, TextareaProps>(
  ({ label, labelText, helpText, error, id, className, ...props }, ref) => {
    const displayLabel = label ?? labelText;
    const vars = textareaVariants({ hasError: !!error });
    return (
      <div className={cn(vars.root(), className)}>
        {displayLabel && (
          <label htmlFor={id} className={vars.label()}>
            {displayLabel}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          className={vars.textarea()}
          {...props}
        />
        {error ? (
          <p className={vars.error()}>{error}</p>
        ) : (
          helpText && <p className={vars.help()}>{helpText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea, textareaVariants };
export type { TextareaProps, TextareaVariants };
